/* eslint-disable enforce-ids-in-jsx/missing-ids */
import React from 'react'
import { Eye } from 'iconsax-react'
import styled from 'styled-components'

import {
  MAX_TRAINING_REMINDERS,
  PARTICIPANT_STATUSES_LABELS,
  PARTICIPANT_STATUSES_VALUES,
  TLP_STATUSES_LABELS,
  TLP_STATUSES_VALUES,
} from 'containers/SpearPhishingAgent/constants'

import { FillCrown, Untrained } from 'components/lib/DesignSystem/Icons'
import IconButton from 'components/lib/DesignSystem/V3/IconButton'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'
import {
  NameText,
  NameTooltipContent,
  NameTooltipEmail,
  NameTooltipName,
  NameTooltipPill,
  NameTooltipPills,
  NameWithSignals,
  RiskSignalChip,
  TableNameContainer,
} from 'components/pages/SpearPhishingAgent/components/Dashboard/components/CardSection/styles'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

import { formatDayDate, formatFullDate } from '../../utils'

/* Same calm status as the training report's participants table: a small coloured dot
   and a default-colour label, no filled pill and no glyph. Tones follow the report's map
   (positive / warning / negative / neutral) so the two tables read as one system. */
const TONE_DOT = {
  positive: 'var(--positive-color)',
  warning: 'var(--warning-color)',
  negative: 'var(--negative-color)',
  neutral: 'var(--t-colors-gray-300, #c2c7d6)',
}

const PARTICIPANT_STATUS_TONE = {
  [PARTICIPANT_STATUSES_VALUES.REPORTED]: 'positive',
  [PARTICIPANT_STATUSES_VALUES.CLICKED]: 'negative',
  [PARTICIPANT_STATUSES_VALUES.OPENED]: 'warning',
  [PARTICIPANT_STATUSES_VALUES.NOT_STARTED]: 'neutral',
}

const TLP_STATUS_TONE = {
  [TLP_STATUSES_VALUES.TRAINED]: 'positive',
  [TLP_STATUSES_VALUES.UNTRAINED]: 'warning',
}

const StatusText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-2);
  color: var(--color-text);
  font-size: var(--t-typography-font-size-sm);
  white-space: nowrap;
`

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tone }) => TONE_DOT[$tone] || TONE_DOT.neutral};
`

const DotStatus = ({ tone, label, dataTestId }) => (
  <StatusText data-testid={dataTestId}>
    <StatusDot $tone={tone} />
    {label}
  </StatusText>
)

/* Same cell as the training report's Reminders column: the last send date, then the
   number of sends in a thin outlined pill when there was more than one. */
const ReminderCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-2);
  color: var(--color-text);
  white-space: nowrap;
`

const ReminderCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: var(--t-border-radius-rounded);
  border: 1px solid var(--color-border);
  font-size: var(--t-typography-font-size-xs);
`

/* The eye hugs the right edge of its cell. */
const EyeCell = styled.span`
  display: flex;
  justify-content: flex-end;
  margin-right: calc(var(--t-spacing-2) * -1);
`

/* Fixed shares of the table, so the columns sit in the same place for every simulation
   instead of following whichever role or department happens to be longest. */
const COLUMN_WIDTHS = {
  employeeInfo: '18%',
  status: '15%',
  tlpStatus: '10%',
  trainingReminderSentDates: '18%',
  sentLanguage: '15%',
  lastUserActivityAt: '17%',
  previewIcon: '56px',
}

/* Department and role sit under the name, one line, truncated rather than wrapped. */
const EmployeeCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-0-5);
  min-width: 0;
  max-width: 100%;
`

const EmployeeMeta = styled.div`
  font-size: var(--t-typography-font-size-xs);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const NameTooltipStory = styled.div`
  margin-top: var(--t-spacing-1);
  font-size: var(--t-typography-font-size-xs);
  color: var(--color-text);
`

const EmptyCell = styled.div`
  color: var(--sub-text-color);
`

/* Dates never break across lines; a squeezed column scrolls instead of stacking a date
   three lines high. */
const DateCell = styled.div`
  white-space: nowrap;
`

const ReminderTooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-1);
`

const ReminderTooltipTitle = styled.div`
  font-weight: var(--t-typography-font-weight-medium);
  margin-bottom: var(--t-spacing-1);
`

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  he: 'Hebrew',
  de: 'German',
}

const getRiskSignals = ({ isVip, tlpStatus }) =>
  [
    isVip && { key: 'vip', variant: 'vip', label: 'VIP', Icon: FillCrown },
    tlpStatus === TLP_STATUSES_VALUES.UNTRAINED && {
      key: 'untrained',
      variant: 'untrained',
      label: 'Training pending',
      Icon: Untrained,
    },
  ].filter(Boolean)

/* One sentence that reads the row back as a story: what this person did, when, and where
   the training stands. Sentence case, no jargon. */
export const describeParticipant = ({ status, tlpStatus, lastUserActivityAt }) => {
  const when = lastUserActivityAt ? ` on ${formatDayDate(lastUserActivityAt)}` : ''
  switch (status) {
    case PARTICIPANT_STATUSES_VALUES.CLICKED:
      if (tlpStatus === TLP_STATUSES_VALUES.TRAINED) {
        return `Clicked${when} and completed the training`
      }
      if (tlpStatus === TLP_STATUSES_VALUES.UNTRAINED) {
        return `Clicked${when}, training still pending`
      }
      return `Clicked${when}`
    case PARTICIPANT_STATUSES_VALUES.REPORTED:
      return `Reported it${when}`
    case PARTICIPANT_STATUSES_VALUES.OPENED:
      return `Opened the email${when}, didn't click`
    case PARTICIPANT_STATUSES_VALUES.NOT_STARTED:
      return "Hasn't opened it yet"
    default:
      return ''
  }
}

const createParticipantColumns = ({ onPreviewClick }) => [
  {
    key: 'employeeInfo',
    header: 'Employee',
    width: COLUMN_WIDTHS.employeeInfo,
    sortable: true,
    sortKey: 'name',
    render: (participant) => {
      const { id, name, email, department, jobTitle, isVip, tlpStatus } = participant
      const signals = getRiskSignals({ isVip, tlpStatus })
      const meta = [department, jobTitle].filter(Boolean).join(' · ')
      const story = describeParticipant(participant)

      return (
        <EmployeeCell>
          <TableNameContainer>
            <Tooltip
              id={`Psima_Launched_EmployeeIdentity_tooltip-${id}`}
              side="top"
              arrow
              content={
                <NameTooltipContent>
                  <NameTooltipName>{name}</NameTooltipName>
                  <NameTooltipEmail>{email}</NameTooltipEmail>
                  {story && <NameTooltipStory>{story}</NameTooltipStory>}
                  {signals.length > 0 && (
                    <NameTooltipPills>
                      {signals.map(({ key, variant, label, Icon }) => (
                        <NameTooltipPill key={key} $variant={variant}>
                          <Icon width="12px" height="12px" fill="currentColor" />
                          {label}
                        </NameTooltipPill>
                      ))}
                    </NameTooltipPills>
                  )}
                </NameTooltipContent>
              }
            >
              <NameWithSignals>
                <NameText data-testid={`Psima_Launched_TableEmployeeName_div-${id}`}>
                  {name}
                </NameText>
                {signals.map(({ key, variant, label, Icon }) => (
                  <RiskSignalChip
                    key={key}
                    $variant={variant}
                    title={label}
                    data-testid={`Psima_Launched_Employee_${key}_chip-${id}`}
                  >
                    <Icon width="13px" height="13px" fill="currentColor" />
                  </RiskSignalChip>
                ))}
              </NameWithSignals>
            </Tooltip>
          </TableNameContainer>
          {meta && (
            <EmployeeMeta title={meta} data-testid={`Psima_Launched_TableEmployeeMeta_div-${id}`}>
              {meta}
            </EmployeeMeta>
          )}
        </EmployeeCell>
      )
    },
  },
  {
    key: 'status',
    header: 'Engagement',
    width: COLUMN_WIDTHS.status,
    sortable: true,
    render: ({ id, status }) => (
      <DotStatus
        dataTestId={`PsimaLaunched_StatusBadge_badge-${id}`}
        tone={PARTICIPANT_STATUS_TONE[status]}
        label={PARTICIPANT_STATUSES_LABELS[status] || '–'}
      />
    ),
  },
  {
    key: 'tlpStatus',
    header: 'Training',
    width: COLUMN_WIDTHS.tlpStatus,
    sortable: true,
    render: ({ id, tlpStatus }) =>
      TLP_STATUS_TONE[tlpStatus] ? (
        <DotStatus
          dataTestId={`PsimaLaunched_TlpStatusBadge_badge-${id}`}
          tone={TLP_STATUS_TONE[tlpStatus]}
          label={TLP_STATUSES_LABELS[tlpStatus]}
        />
      ) : (
        <EmptyCell data-testid={`PsimaLaunched_TlpStatus_empty-div-${id}`}>–</EmptyCell>
      ),
  },
  /* Its own column again (Natali, 3.9): folded under Training it could not be sorted on.
     Header and cell follow the training report's Reminders column. */
  {
    key: 'trainingReminderSentDates',
    header: 'Reminders',
    width: COLUMN_WIDTHS.trainingReminderSentDates,
    sortable: true,
    render: ({ id, trainingReminderSentDates }) => {
      if (!trainingReminderSentDates?.length) {
        return <EmptyCell data-testid={`PsimaLaunched_TlpReminder_empty-div-${id}`}>–</EmptyCell>
      }

      const count = trainingReminderSentDates.length
      const latestSentAt = trainingReminderSentDates[count - 1]
      const title =
        count > MAX_TRAINING_REMINDERS
          ? `Sent ${count}`
          : `Sent ${count} of ${MAX_TRAINING_REMINDERS}`

      return (
        <Tooltip
          id={`PsimaLaunched_TlpReminder_tooltip-${id}`}
          side="top"
          arrow
          content={
            <ReminderTooltipList>
              <ReminderTooltipTitle>{title}</ReminderTooltipTitle>
              {trainingReminderSentDates.map((date) => (
                <div key={date}>{formatFullDate(date)}</div>
              ))}
            </ReminderTooltipList>
          }
        >
          <ReminderCell data-testid={`PsimaLaunched_TlpReminder_div-${id}`}>
            {formatDayDate(latestSentAt)}
            {count > 1 && <ReminderCount>{count}</ReminderCount>}
          </ReminderCell>
        </Tooltip>
      )
    },
  },
  {
    key: 'sentLanguage',
    header: 'Sent language',
    width: COLUMN_WIDTHS.sentLanguage,
    sortable: true,
    render: ({ id, sentLanguage }) =>
      sentLanguage ? (
        <div data-testid={`PsimaLaunched_SentLanguage_div-${id}`}>
          {LANGUAGE_LABELS[sentLanguage] || sentLanguage}
        </div>
      ) : (
        <EmptyCell data-testid={`PsimaLaunched_SentLanguage_empty-div-${id}`}>–</EmptyCell>
      ),
  },
  {
    key: 'lastUserActivityAt',
    header: 'Last activity',
    width: COLUMN_WIDTHS.lastUserActivityAt,
    sortable: true,
    render: ({ id, lastUserActivityAt }) =>
      lastUserActivityAt ? (
        <DateCell>{formatFullDate(lastUserActivityAt)}</DateCell>
      ) : (
        <EmptyCell data-testid={`PsimaLaunched_LastActivity_empty-div-${id}`}>–</EmptyCell>
      ),
  },
  {
    key: 'previewIcon',
    header: '',
    width: COLUMN_WIDTHS.previewIcon,
    /* Same eye as the incidents list: iconsax Eye 16 in a ghost IconButton. */
    render: (row) => (
      <Tooltip
        id={`Psima_Launched_TablePreview_tooltip-${row.id}`}
        side="top"
        content={
          row.isEmailReady
            ? 'Preview the email this employee received'
            : 'The email is still being prepared'
        }
      >
        <EyeCell>
          <IconButton
            id={`Psima_Launched_TablePreview_button-${row.id}`}
            ariaLabel="Preview email"
            size="small"
            variant="ghost"
            dataTestId={`Psima_Launched_TablePreviewIcon_div-${row.profileId}`}
            dataPendoFeature={PSIMA_PENDO_FEATURES.LAUNCHED_EMAIL_PREVIEW}
            disabled={!row.isEmailReady}
            onClick={(event) => onPreviewClick(row, event)}
          >
            <Eye size={16} variant="Outline" />
          </IconButton>
        </EyeCell>
      </Tooltip>
    ),
  },
]

export default createParticipantColumns
